/**
 * O cliente da API `/v1`, instanciado com a credencial desta aba.
 *
 * ── Por que passa por aqui e não é criado em cada tela ────────────────────
 *
 * O `criarCliente` do `@cliqcrm/contracts` recebe uma FUNÇÃO que devolve o
 * token, e não o token. O cabeçalho dele explica: o token dura 15 minutos e é
 * renovado em segundo plano; guardar o valor faria a tela segurar um token
 * vencido pelo resto da sessão.
 *
 * Essa função é a `tokenDaSessao`, e ela mora em `shared/sessao/token-da-sessao`
 * — não aqui. Está lá porque o fluxo de eventos (SSE) precisa exatamente da
 * mesma, e enquanto cada um tinha a sua, a do fluxo ficou sem a renovação e o
 * tempo real caiu sem ninguém ver. Ver o cabeçalho de lá.
 */

import { criarCliente, type ClienteDaApi } from '@cliqcrm/contracts';
import { tokenDaSessao } from '@/shared/sessao/token-da-sessao';

/*
 * `SemSessaoError` mudou de casa e continua sendo exportada daqui.
 *
 * Ela foi junto com a função que a lança, e quem a importava deste módulo
 * continua importando — mover o erro e o `import` de dezenas de telas na mesma
 * mudança misturaria dois assuntos.
 */
export { SemSessaoError } from '@/shared/sessao/token-da-sessao';

/*
 * A trava de "uma renovação de cada vez" mora em `fluxo-oidc`, e não aqui.
 *
 * Ela nasceu neste arquivo e protegia só as renovações que vêm de uma
 * requisição — o provedor de sessão chamava por fora, e os dois competiam pelo
 * mesmo pedido no `sessionStorage`. Uma trava por chamador protege só aquele
 * chamador.
 */

/**
 * `revenda` nula é o PRIMEIRO passo do login.
 *
 * O token do cofre, nesse momento, prova quem a pessoa é e não diz onde ela
 * entra — e serve uma rota só: `GET /v1/entrada/organizacoes`. Qualquer outra
 * recusa com 401, porque não há `tenant_id` para abrir o contexto.
 */
export function criarClienteDaSessao(revenda: string | null): ClienteDaApi {
  return criarCliente({
    // Mesma origem: em produção a API responde atrás do mesmo domínio, e em
    // desenvolvimento o proxy do Vite faz o mesmo. Uma base configurável aqui
    // seria um caminho de exceção que só existe na máquina de quem desenvolve.
    token: () => tokenDaSessao(revenda),
  });
}
